import { useRef } from 'react'

/**
 * Custom hook to stabilize array references to prevent unnecessary re-renders.
 * Returns the same array reference unless the array contents actually change.
 *
 * This is useful for preventing infinite loops in hooks that depend on arrays
 * passed as props, where the parent component may create a new array reference
 * on every render even if the contents are identical.
 *
 * @template T - The type of items in the array
 * @param array - The input array to stabilize
 * @returns A stable array reference that only changes when contents change
 *
 * @example
 * const stableIds = useStableArray(channelIds)
 * useEffect(() => {
 *   // This will only run when channelIds contents actually change
 *   fetchData(stableIds)
 * }, [stableIds])
 */
export function useStableArray<T>(array: T[]): T[] {
  const ref = useRef(array)

  if (
    array.length !== ref.current.length ||
    array.some((item, i) => item !== ref.current[i])
  ) {
    ref.current = array
  }

  return ref.current
}
